<template>
  <section class="relative isolate min-h-[100svh] overflow-hidden bg-[#090807] text-[#f3ede3]">
    <div class="absolute inset-0 bg-[#090807]"></div>
    <div
      class="absolute inset-y-0 right-0 left-0 sm:left-[10%] lg:left-[26%]"
      :style="heroMediaStyle"
    >
      <KImage
        v-if="imageSrc && !videoSrc"
        :src="imageSrc"
        :alt="copy.imageAlt"
        class="h-full w-full object-cover"
        :mobile-focal-x="imageFocal?.x ?? 50"
        :mobile-focal-y="imageFocal?.y ?? 38"
        :desktop-focal-x="imageFocal?.x ?? 52"
        :desktop-focal-y="imageFocal?.y ?? 42"
        width="1473"
        height="2210"
        sizes="sm:100vw md:100vw lg:74vw xl:74vw 2xl:74vw"
        quality="72"
        format="webp"
        above-fold
      />
      <video
        v-if="videoSrc"
        ref="heroVideo"
        class="home-hero-video absolute inset-0 h-full w-full object-cover"
        autoplay
        muted
        loop
        playsinline
        webkit-playsinline
        :poster="videoPoster"
        preload="metadata"
        aria-hidden="true"
        disablepictureinpicture
        disableremoteplayback
        @canplay="playHeroVideo"
        @loadeddata="playHeroVideo"
      >
        <source v-if="resolvedVideoSrc" :src="resolvedVideoSrc" type="video/mp4">
      </video>
    </div>

    <div
      class="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,7,6,0.72)_0%,rgba(8,7,6,0.18)_26%,rgba(8,7,6,0.08)_52%,rgba(8,7,6,0.72)_100%),linear-gradient(90deg,rgba(8,7,6,0.82)_0%,rgba(8,7,6,0.42)_30%,rgba(8,7,6,0.08)_58%,rgba(8,7,6,0)_76%)]"
    ></div>

    <button
      v-if="videoSrc"
      type="button"
      class="home-hero-sound-button absolute -mt-[0.9rem] right-6 top-[7.25rem] z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#fbf5eb]/20 bg-black/25 text-[#fbf5eb] backdrop-blur-sm transition hover:border-[#fbf5eb]/45 hover:bg-black/35 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-[#fbf5eb]/70 sm:right-8 sm:top-32 lg:left-[calc(26%+3rem)] lg:right-auto lg:top-[10.5rem]"
      :aria-label="videoSoundLabel"
      :title="videoSoundLabel"
      @click="toggleVideoSound"
    >
      <VolumeX v-if="isVideoMuted" class="h-4 w-4" aria-hidden="true" :stroke-width="1.75" />
      <Volume2 v-else class="h-4 w-4" aria-hidden="true" :stroke-width="1.75" />
    </button>

    <nav
      class="absolute left-6 top-[7.25rem] z-10 flex max-w-[calc(100%-6rem)] flex-col gap-y-3 sm:left-8 sm:top-32 lg:left-auto lg:right-[calc(74%+3.5rem)] lg:top-[10.5rem] lg:block lg:w-48 lg:text-right"
      :aria-label="heroNavLabel"
    >
      <NuxtLink
        v-for="item in collectionMenuItems"
        :key="item.to"
        :to="item.to"
        no-prefetch
        class="block text-[0.62rem] uppercase tracking-[0.3em] text-[#f3ede3]/52 transition hover:text-[#fbf5eb] focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-[#fbf5eb]/70 lg:mb-5"
      >
        {{ item.label }}
      </NuxtLink>
    </nav>

    <div class="relative mx-auto flex min-h-[100svh] max-w-[1680px] items-end px-6 pb-12 pt-28 sm:px-8 sm:pb-14 md:pt-32 lg:px-14 lg:pb-20">
      <div class="max-w-[25rem]">
        <p class="text-[0.62rem] uppercase tracking-[0.36em] text-[#f3ede3]/54">
          {{ copy.eyebrow }}
        </p>

        <button
          v-if="hasCurrentOpening"
          type="button"
          class="mt-4 inline-flex border border-[#fbf5eb]/24 px-3 py-1.5 text-[0.58rem] uppercase tracking-[0.28em] text-[#fbf5eb]/78"
          @click="scrollToCurrentOpenings"
        >
          {{ copy.currentOpening }}
        </button>

        <h1 class="mt-5 max-w-[9ch] font-['Playfair_Display'] text-[3.3rem] leading-[0.8] tracking-[-0.01em] text-[#fbf5eb] sm:text-[4.5rem] lg:text-[6.3rem]">
          {{ copy.title }}
        </h1>

        <p class="mt-6 max-w-[16rem] text-[0.78rem] uppercase tracking-[0.24em] text-[#f3ede3]/62 sm:text-[0.82rem]">
          {{ copy.body }}
        </p>

        <HomeInstagramWelcome />

        <div :class="fromInstagram ? 'mt-4 flex' : 'mt-10 flex'">
          <button
            type="button"
            :class="[
              'inline-flex min-h-12 items-center justify-center whitespace-nowrap rounded-full border px-7 py-3 text-center text-[0.72rem] uppercase tracking-[0.26em] transition focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-[#fbf5eb]/80',
              fromInstagram
                ? 'border-[#fbf5eb]/45 bg-transparent text-[#fbf5eb] hover:border-[#fbf5eb] hover:bg-[#fbf5eb]/10'
                : 'border-[#fbf5eb]/70 bg-[#f4ecdf] text-[#0c0a08] shadow-[0_16px_44px_rgba(0,0,0,0.28)] hover:border-white hover:bg-white'
            ]"
            @click="scrollToCollections"
          >
            {{ copy.secondaryCta }}
          </button>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="absolute bottom-6 right-6 inline-flex h-12 w-6 items-center justify-center text-[#fbf5eb]/42 transition hover:text-[#fbf5eb]/68 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-[#fbf5eb]/70 sm:left-1/2 sm:right-auto sm:-translate-x-1/2"
      :aria-label="copy.scrollCta"
      @click="scrollToCurrentOpenings"
    >
      <span class="home-hero-scroll-arrow block h-5 w-2.5" aria-hidden="true">
        <span class="absolute left-1/2 top-0 h-4 w-px -translate-x-1/2 bg-current"></span>
        <span class="absolute bottom-0 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-b border-r border-current"></span>
      </span>
    </button>
  </section>
</template>

<script setup lang="ts">
import { Volume2, VolumeX } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useHomeLuxuryContext } from '~/composables/useHomeLuxuryContext'

const props = withDefaults(defineProps<{
  currentState?: 'open' | 'closed'
  hasCurrentOpening?: boolean
  imageFocal?: { x?: number; y?: number } | null
  imageSrc?: string
  videoSrc?: string
}>(), {
  currentState: 'closed',
  hasCurrentOpening: false,
  imageFocal: null,
  imageSrc: '',
  videoSrc: ''
})

const { currentLocale, localePrefix } = useHomeLuxuryContext()
const { network: socialNetwork } = useSocialOrigin()
const fromInstagram = computed(() => socialNetwork.value === 'instagram')
const heroVideo = ref<HTMLVideoElement | null>(null)
const isVideoMuted = ref(true)
const resolvedVideoSrc = ref('')
const videoPoster = computed(() => props.videoSrc.replace(/\.mp4$/, '-poster.jpg'))
let videoStartTimers: ReturnType<typeof setTimeout>[] = []

const NAV_LABELS = {
  en: ['Collection', 'The House', 'Journal', 'Music', 'Wholesale'],
  fr: ['Collection', 'La Maison', 'Journal', 'Musique', 'Wholesale'],
  es: ['Colección', 'La Casa', 'Journal', 'Música', 'Wholesale'],
  pt: ['Coleção', 'A Casa', 'Journal', 'Música', 'Wholesale'],
  ja: ['コレクション', 'メゾン', 'ジャーナル', '音楽', 'ホールセール'],
  ko: ['컬렉션', '메종', '저널', '음악', '홀세일'],
  de: ['Kollektion', 'Das Haus', 'Journal', 'Musik', 'Wholesale'],
  it: ['Collezione', 'La Maison', 'Journal', 'Musica', 'Wholesale'],
  zh: ['系列', '品牌', '日志', '音乐', '批发']
} as const

const HERO_NAV_LABELS = {
  en: 'Collections and brand',
  fr: 'Collections et marque',
  es: 'Colecciones y marca',
  pt: 'Coleções e marca',
  ja: 'コレクションとブランド',
  ko: '컬렉션 및 브랜드',
  de: 'Kollektionen und Marke',
  it: 'Collezioni e brand',
  zh: '系列与品牌'
} as const

const HOME_HERO_COPY = {
  en: {
    bodyClosed: 'ALIGN 110 — Paris / Made in Italy',
    bodyOpen: 'ALIGN 110 — Paris / Made in Italy',
    collectionNavLabel: 'Collections',
    currentOpening: 'Current opening',
    eyebrowClosed: 'Kendrick Paris',
    eyebrowOpen: 'Kendrick Paris',
    imageAlt: 'Kendrick Paris black heeled ankle boot in editorial light',
    primaryClosed: 'Join the list',
    primaryOpen: 'Access the opening',
    scrollCta: 'Scroll',
    secondary: 'Discover ALIGN 110',
    title: 'The ankle boot.'
  },
  fr: {
    bodyClosed: 'ALIGN 110 — Paris / Made in Italy',
    bodyOpen: 'ALIGN 110 — Paris / Made in Italy',
    collectionNavLabel: 'Collections',
    currentOpening: 'Ouverture en cours',
    eyebrowClosed: 'Kendrick Paris',
    eyebrowOpen: 'Kendrick Paris',
    imageAlt: 'Kendrick Paris bottine noire à talon en lumière éditoriale',
    primaryClosed: 'Rejoindre la liste',
    primaryOpen: 'Accéder à l’ouverture',
    scrollCta: 'Défiler',
    secondary: 'Découvrir ALIGN 110',
    title: 'La bottine.'
  },
  es: {
    bodyClosed: 'ALIGN 110 — Paris / Made in Italy',
    bodyOpen: 'ALIGN 110 — Paris / Made in Italy',
    collectionNavLabel: 'Colecciones',
    currentOpening: 'Apertura en curso',
    eyebrowClosed: 'Kendrick Paris',
    eyebrowOpen: 'Kendrick Paris',
    imageAlt: 'Botín negro Kendrick Paris con tacón en luz editorial',
    primaryClosed: 'Unirse a la lista',
    primaryOpen: 'Acceder a la apertura',
    scrollCta: 'Desplazar',
    secondary: 'Descubrir ALIGN 110',
    title: 'El botín.'
  },
  pt: {
    bodyClosed: 'ALIGN 110 — Paris / Made in Italy',
    bodyOpen: 'ALIGN 110 — Paris / Made in Italy',
    collectionNavLabel: 'Coleções',
    currentOpening: 'Abertura atual',
    eyebrowClosed: 'Kendrick Paris',
    eyebrowOpen: 'Kendrick Paris',
    imageAlt: 'Botim preto Kendrick Paris com salto em luz editorial',
    primaryClosed: 'Entrar na lista',
    primaryOpen: 'Acessar a abertura',
    scrollCta: 'Rolar',
    secondary: 'Descobrir ALIGN 110',
    title: 'O botim.'
  },
  ja: {
    bodyClosed: 'ALIGN 110 — Paris / Made in Italy',
    bodyOpen: 'ALIGN 110 — Paris / Made in Italy',
    collectionNavLabel: 'コレクション',
    currentOpening: '現在オープン中',
    eyebrowClosed: 'Kendrick Paris',
    eyebrowOpen: 'Kendrick Paris',
    imageAlt: 'エディトリアルな光の中のKendrick Paris黒ヒールアンクルブーツ',
    primaryClosed: 'リストに参加',
    primaryOpen: 'アクセスする',
    scrollCta: 'スクロール',
    secondary: 'ALIGN 110を見る',
    title: 'アンクルブーツ。'
  },
  ko: {
    bodyClosed: 'ALIGN 110 — Paris / Made in Italy',
    bodyOpen: 'ALIGN 110 — Paris / Made in Italy',
    collectionNavLabel: '컬렉션',
    currentOpening: '현재 오픈',
    eyebrowClosed: 'Kendrick Paris',
    eyebrowOpen: 'Kendrick Paris',
    imageAlt: '에디토리얼 조명 속 Kendrick Paris 블랙 힐 앵클부츠',
    primaryClosed: '리스트 참여',
    primaryOpen: '오픈 보기',
    scrollCta: '스크롤',
    secondary: 'ALIGN 110 보기',
    title: '앵클부츠.'
  },
  de: {
    bodyClosed: 'ALIGN 110 — Paris / Made in Italy',
    bodyOpen: 'ALIGN 110 — Paris / Made in Italy',
    collectionNavLabel: 'Kollektionen',
    currentOpening: 'Aktuelle Öffnung',
    eyebrowClosed: 'Kendrick Paris',
    eyebrowOpen: 'Kendrick Paris',
    imageAlt: 'Kendrick Paris schwarzer Ankle Boot mit Absatz in editorialem Licht',
    primaryClosed: 'Liste beitreten',
    primaryOpen: 'Zugang öffnen',
    scrollCta: 'Scrollen',
    secondary: 'ALIGN 110 entdecken',
    title: 'Der Ankle Boot.'
  },
  it: {
    bodyClosed: 'ALIGN 110 — Paris / Made in Italy',
    bodyOpen: 'ALIGN 110 — Paris / Made in Italy',
    collectionNavLabel: 'Collezioni',
    currentOpening: 'Apertura in corso',
    eyebrowClosed: 'Kendrick Paris',
    eyebrowOpen: 'Kendrick Paris',
    imageAlt: 'Tronchetto nero con tacco Kendrick Paris in luce editoriale',
    primaryClosed: 'Entrare in lista',
    primaryOpen: 'Accedere all’apertura',
    scrollCta: 'Scorri',
    secondary: 'Scoprire ALIGN 110',
    title: 'Il tronchetto.'
  },
  zh: {
    bodyClosed: 'ALIGN 110 — Paris / Made in Italy',
    bodyOpen: 'ALIGN 110 — Paris / Made in Italy',
    collectionNavLabel: '系列',
    currentOpening: '当前开放',
    eyebrowClosed: 'Kendrick Paris',
    eyebrowOpen: 'Kendrick Paris',
    imageAlt: '编辑光线中的 Kendrick Paris 黑色高跟短靴',
    primaryClosed: '加入名单',
    primaryOpen: '进入开放',
    scrollCta: '滚动',
    secondary: '探索 ALIGN 110',
    title: '短靴。'
  }
} as const

const VIDEO_SOUND_COPY = {
  en: {
    mute: 'Mute video',
    unmute: 'Turn video sound on'
  },
  fr: {
    mute: 'Couper le son',
    unmute: 'Activer le son'
  },
  es: {
    mute: 'Silenciar video',
    unmute: 'Activar sonido'
  },
  pt: {
    mute: 'Silenciar video',
    unmute: 'Ativar som'
  },
  ja: {
    mute: '動画をミュート',
    unmute: '動画の音声をオン'
  },
  ko: {
    mute: '비디오 음소거',
    unmute: '비디오 소리 켜기'
  },
  de: {
    mute: 'Video stummschalten',
    unmute: 'Videoton einschalten'
  },
  it: {
    mute: 'Disattiva audio video',
    unmute: 'Attiva audio video'
  },
  zh: {
    mute: '视频静音',
    unmute: '开启视频声音'
  }
} as const

const collectionMenuItems = computed(() => {
  const prefix = localePrefix.value
  const labels = NAV_LABELS[currentLocale.value] || NAV_LABELS.en
  const isFr = currentLocale.value === 'fr'
  return [
    { label: labels[0], to: isFr ? '/fr/collection' : '/collection' },
    { label: labels[1], to: isFr ? '/fr/brand' : '/brand' },
    { label: labels[2], to: isFr ? '/fr/magazine' : '/magazine' },
    { label: labels[3], to: isFr ? '/fr/musique' : '/music' },
    { label: labels[4], to: isFr ? '/fr/professionnels' : '/wholesales' }
  ]
})

const heroNavLabel = computed(() => HERO_NAV_LABELS[currentLocale.value] || HERO_NAV_LABELS.en)

const normalizeFocal = (value: number | undefined, fallback: number) => {
  const focal = value ?? fallback
  return `${Math.min(100, Math.max(0, focal))}%`
}

const heroMediaStyle = computed(() => ({
  '--home-hero-mobile-focal-x': normalizeFocal(props.imageFocal?.x, 50),
  '--home-hero-mobile-focal-y': normalizeFocal(props.imageFocal?.y, 38),
  '--home-hero-desktop-focal-x': normalizeFocal(props.imageFocal?.x, 52),
  '--home-hero-desktop-focal-y': normalizeFocal(props.imageFocal?.y, 42)
}))

const videoSoundCopy = computed(() => VIDEO_SOUND_COPY[currentLocale.value] || VIDEO_SOUND_COPY.en)
const videoSoundLabel = computed(() => (
  isVideoMuted.value ? videoSoundCopy.value.unmute : videoSoundCopy.value.mute
))

const scrollToCollections = () => {
  if (!import.meta.client) return

  document.getElementById('permanent-collections')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  })
}

const scrollToCurrentOpenings = () => {
  if (!import.meta.client) return

  const target = document.getElementById('preorder-drops') || document.getElementById('permanent-collections')
  target?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  })
}

// Picks the smallest hero encode that still looks sharp: viewport (device px), data saver and slow networks.
const pickVideoVariant = () => {
  const base = props.videoSrc.replace(/\.mp4$/, '')
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean, effectiveType?: string } }).connection
  const slow = Boolean(connection?.saveData) || /(^|-)2g$|3g/.test(connection?.effectiveType || '')
  const pixels = Math.max(window.innerWidth, window.innerHeight) * Math.min(window.devicePixelRatio || 1, 2)

  if (slow || pixels <= 900) return `${base}-360.mp4`
  if (pixels <= 1500) return `${base}-540.mp4`
  return `${base}-720.mp4`
}

const playHeroVideo = async () => {
  const video = heroVideo.value
  if (!video || !isVideoMuted.value) return

  video.muted = true
  video.volume = 0

  try {
    await video.play()
  } catch {
    // iOS may still require a user gesture; the first page touch retries playback.
  }
}

const toggleVideoSound = async () => {
  const video = heroVideo.value
  if (!video) return

  const wasPaused = video.paused
  const nextMuted = !isVideoMuted.value
  video.muted = nextMuted
  video.volume = nextMuted ? 0 : 1
  isVideoMuted.value = nextMuted

  try {
    await video.play()
  } catch {
    if (!nextMuted && wasPaused) {
      video.muted = true
      video.volume = 0
      isVideoMuted.value = true
    }
  }
}

const queueVideoStartAttempts = () => {
  videoStartTimers = [0, 350, 1200, 2500].map(delay =>
    setTimeout(() => {
      void playHeroVideo()
    }, delay)
  )
}

onMounted(async () => {
  if (!import.meta.client) return

  if (props.videoSrc) {
    resolvedVideoSrc.value = pickVideoVariant()
    await nextTick()
    heroVideo.value?.load()
  }

  queueVideoStartAttempts()
  window.addEventListener('touchstart', playHeroVideo, { once: true, passive: true })
  window.addEventListener('pointerdown', playHeroVideo, { once: true })
})

onBeforeUnmount(() => {
  for (const timer of videoStartTimers) {
    clearTimeout(timer)
  }

  videoStartTimers = []

  if (!import.meta.client) return
  window.removeEventListener('touchstart', playHeroVideo)
  window.removeEventListener('pointerdown', playHeroVideo)
})

const copy = computed(() => {
  const localizedCopy = HOME_HERO_COPY[currentLocale.value] || HOME_HERO_COPY.en

  return {
    body: props.currentState === 'open' ? localizedCopy.bodyOpen : localizedCopy.bodyClosed,
    collectionNavLabel: localizedCopy.collectionNavLabel,
    currentOpening: localizedCopy.currentOpening,
    eyebrow: props.currentState === 'open' ? localizedCopy.eyebrowOpen : localizedCopy.eyebrowClosed,
    imageAlt: localizedCopy.imageAlt,
    scrollCta: localizedCopy.scrollCta,
    secondaryCta: localizedCopy.secondary,
    title: localizedCopy.title
  }
})
</script>

<style scoped>
.home-hero-video {
  object-position: var(--home-hero-mobile-focal-x, 50%) var(--home-hero-mobile-focal-y, 38%);
}

@media (min-width: 1024px) {
  .home-hero-video {
    object-position: var(--home-hero-desktop-focal-x, 52%) var(--home-hero-desktop-focal-y, 42%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-hero-video,
  .home-hero-sound-button {
    display: none;
  }

  .home-hero-scroll-arrow {
    animation: none;
  }
}

.home-hero-scroll-arrow {
  animation: scrollArrowDrift 2.8s ease-in-out infinite;
  position: relative;
}

@keyframes scrollArrowDrift {
  0%, 100% { opacity: 0.48; transform: translateY(0); }
  50% { opacity: 0.78; transform: translateY(5px); }
}
</style>
